import { ErrorBoundary } from '@sentry/react';
import { ButtonIcon, Typography, SearchField } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { isSearchQueryLoadingAtom, searchQueryResultsAtom } from '@/atoms/queries/search';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { SearchResults } from './SearchResults';
import { useSearchDropdown } from './useSearchDropdown';

const TopBarSearchResults = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('search');
  const { player, team } = useAtomValue(searchQueryResultsAtom);

  return <SearchResults label={t('results')} searchResults={{ player, team }} onClick={onClick} />;
};

export const Search = () => {
  const isTouch = useIsTouchDevice();
  const { t } = useTranslation(['search', 'general']);
  const isLoading = useAtomValue(isSearchQueryLoadingAtom);
  const {
    searchValue,
    isResultsOpen,
    wrapperRef,
    openResults,
    clearSearch,
    changeSearch,
    selectResult,
    handleKeyDown,
    handleBlur,
  } = useSearchDropdown();
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  return (
    <>
      {isTouch && (
        <ButtonIcon
          title={t('search')}
          displayText="hidden"
          icon="Search"
          onClick={() => setIsMobileSearchOpen(true)}
          variant="ghost"
        />
      )}

      <div
        className={twJoin(isMobileSearchOpen ? 'mobile-search-open' : 'hidden', 'search-wrapper md:block')}
        ref={wrapperRef}
        role="presentation"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        {isTouch && isMobileSearchOpen && (
          <div className="flex w-full justify-between items-center gap-4">
            <Typography variant="headingRegular" data-testid="search-heading">
              {t('search')}
            </Typography>
            <ButtonIcon
              title={t('close', { ns: 'general' })}
              displayText="hidden"
              icon="Close"
              onClick={() => setIsMobileSearchOpen(false)}
              variant="ghost"
            />
          </div>
        )}
        <div className="search-field-wrapper">
          <SearchField
            isLoading={isLoading}
            clearTitle={t('clear', { ns: 'general' })}
            isFocused={isMobileSearchOpen}
            searchIconTitle={t('search')}
            onClear={clearSearch}
            onClick={openResults}
            onChange={changeSearch}
            placeholder={t('placeholder', { ns: 'search' })}
            value={searchValue}
          />
        </div>
        {isResultsOpen && (
          <ErrorBoundary>
            <Suspense fallback={null}>
              <TopBarSearchResults
                onClick={() => {
                  selectResult();
                  setIsMobileSearchOpen(false);
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </div>
    </>
  );
};
